import { useEffect, useState } from 'react';
import { Table, Button, Row, Col, Popconfirm, Space, Tooltip, Select } from 'antd';
import { PlusOutlined, FilePdfOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import InvoiceExport from './invoiceExport';
import { formatPrice, getTaxStatus, getInvoiceStatus, getAllInvoiceStatus } from '../../../utils/common';
import styles from './invoiceList.module.scss';
import { InvoiceStatusCapture } from '../../../components/InvoiceStatusCapture';

export default function InvoiceList({
  invoices,
  customers,
  onAdd,
  loading,
  submitInvoice,
  submitInvoiceStatus,
  fetchInvoices,
  setInvoices,
}) {

  const columns = [
    // { title: 'No', dataIndex: 'id', key: 'id' },
    { 
      title: 'Customer', 
      dataIndex: 'customer', 
      key: 'customer',
      render: (value) => {
        const split = String(value.name).split('\n');
        return split.map((line, idx) => {
          let lineItem = line;
          if (idx === split.length - 1) {
            lineItem = `${line} (${value.code})`
          }
          return <div key={idx}>{lineItem}</div>
        });
      },
      sorter: (a, b) => a.customer.name.localeCompare(b.customer.name),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      render: (value) => dayjs(value).format('MM/DD/YYYY'),
      sorter: (a, b) => dayjs(a.invoice_date).valueOf() - dayjs(b.invoice_date).valueOf(),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Invoice Due Date',
      dataIndex: 'invoice_due_date',
      key: 'invoice_due_date',
      render: (value) => dayjs(value).format('MM/DD/YYYY'),
      sorter: (a, b) => dayjs(a.invoice_due_date).valueOf() - dayjs(b.invoice_due_date).valueOf(),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Invoice Price',
      key: 'invoice_price',
      render: (_, record) => {
        const salePrice = +record.sub_total - +record.total_discount;
        return formatPrice(salePrice);
      },
    },
    {
      title: 'Estimated Tax',
      key: 'estimated_tax',
      render: (_, record) => {
        if (record.avalara_response?.summary?.length > 0) {
          let tax = 0;
          record.avalara_response.summary.map(taxSummary => {
            if (+taxSummary.tax > 0) {
              tax += +taxSummary.tax;
            }
          })
          return formatPrice(tax);
        }
        return 'NA';
      },
    },
    {
      title: 'Payments/Credit',
      dataIndex: 'credits',
      key: 'credits',
      render: (value) => formatPrice(value),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      render: (_, record) => {
        let total = +record.total;
        if (record.avalara_response?.summary?.length > 0) {
          record.avalara_response.summary.map(taxSummary => {
            if (+taxSummary.tax > 0) {
              total += +taxSummary.tax;
            }
          })
        }
        return formatPrice(total);
      },
    },
    {
      title: 'Tax Status',
      // dataIndex: 'status',
      key: 'status',
      render: (_, record) => {
        let value = record.status;
        if (!record.is_taxed) {
          value = -1;
        }
        return getTaxStatus(value)
      },
      sorter: (a, b) => {
        const aStatus = getInvoiceStatus(a.status);
        const bStatus = getInvoiceStatus(b.status);
        return aStatus.localeCompare(bStatus);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Invoice Status',
      key: 'invoice_status',
      render: (_, record) => {
        const { invoice_status, invoice_pay_date } = record;
        const statusText = getInvoiceStatus(+invoice_status);
        if (+invoice_status !== 0) {
          const messageData = +invoice_status === 1 ? 'Submit Date:' : 'Paid Date:'
          return <>
            <Tooltip title={ `${messageData} ${dayjs(invoice_pay_date).format('MM/DD/YYYY')}` }>
              {statusText}
            </Tooltip>
          </>
        }
        return statusText;
      },
      sorter: (a, b) => {
        const aStatus = getInvoiceStatus(a.invoice_status);
        const bStatus = getInvoiceStatus(b.invoice_status);
        return aStatus.localeCompare(bStatus);
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Invoice Actions', key: 'actions',
      render: (_, record) => (<Space gap="small" wrap>
        <Button icon={<FilePdfOutlined />} onClick={() => submitInvoice(record.id)}>Download</Button>
        {
          <>
          {(record.invoice_status === 0) && (
            <InvoiceStatusCapture onConfirm={submitInvoiceStatus} record_id={record.id} status={1}/>
          )}
          {(record.invoice_status === 1) && (
            <InvoiceStatusCapture onConfirm={submitInvoiceStatus} record_id={record.id} status={2}/>
          )}
          </>
        }
        {
          (record.is_taxed && (+record.status === 0 || +record.status === 3)) && (
            <>
              <Popconfirm title='Are you sure (Commit)?' onConfirm={() => submitInvoice(record.id, 1)}>
                <Button icon={< CheckCircleOutlined />}>Commit</Button>
              </Popconfirm>
              <Popconfirm title='Are you sure (Void)?' onConfirm={() => submitInvoice(record.id, 2)}>
                <Button icon={< CloseCircleOutlined />}>Void</Button>
              </Popconfirm>
            </>
          )
        }
      </Space>
    )
  }];

  const currentDate = dayjs();

  const rowClassName = (record) => {
    if (+record.invoice_status === 2) {
      return styles.invoicePaid;
    } else if (+record.invoice_status === 0) {
      return styles.invoiceDraft;
    } else {
      if (currentDate.valueOf() > dayjs(record.invoice_due_date).valueOf()) {
        return styles.invoiceOverdue;
      } else {
        return styles.invoicePending;
      }
    }
  }

  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterInvoiceStatus, setFilterInvoiceStatus] = useState('');

  useEffect(() => {
    handleFilterChanges();
  }, [filterCustomer, filterInvoiceStatus])
  const handleFilterChanges = () => {
    const params = {};
    if (filterCustomer) {
      params.customer_id = filterCustomer;
    }
    if (filterInvoiceStatus >= 0) {
      params.invoice_status = filterInvoiceStatus;
    }
    const newData = fetchInvoices(params);
    setInvoices(newData.invoices);
  }

  return (
    <>
      <Row align={'middle'}>
        <Col xs={{span: 24, order: 2 }} sm={{span: 16, order: 1 }}>
          <Row gutter={[8, 8]} className='mb-16'>
            <Col>
              Select a Customer: <Select
                placeholder="Select a Customer"
                style={{ width: '200px' }}
                onChange={setFilterCustomer}
                value={filterCustomer}
                showSearch
                optionFilterProp='children'
              >
                <Select.Option value=''>All</Select.Option>
                {
                  customers.map((c, i) => {
                    return <Select.Option key={i} value={c.id}>{`${c.name} (${c.code})`}</Select.Option>
                  })
                }
              </Select>
            </Col>
            <Col>
              Select an Invoice Status: <Select
                placeholder="Select an Invoice Status"
                style={{ width: '200px' }}
                onChange={setFilterInvoiceStatus}
                value={filterInvoiceStatus}
              >
                <Select.Option value=''>All</Select.Option>
                {
                  getAllInvoiceStatus().map((c, i) => {
                    return <Select.Option key={i} value={c.value}>{c.label}</Select.Option>
                  })
                }
              </Select>
            </Col>
          </Row>
        </Col>
        <Col xs={{span: 24, order: 1 }} sm={{span: 8, order: 2 }}>
          <Row gutter={[8, 8]}justify={'end'} className='mb-16'>
            <Col>
              <InvoiceExport data={invoices}/>
            </Col>
            <Col>
              <Button type="primary" onClick={onAdd} icon={<PlusOutlined />}>
                GENERATE INVOICE
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Table
        dataSource={invoices}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ x: 'max-content' }}
        size="small"
        pagination={invoices?.length > 10 || false}
        rowClassName={rowClassName}
      />
    </>
  );
}
